import React from 'react';
import { X, ExternalLink } from 'lucide-react';
import { Transaction } from '../../services/api';
import { CopyButton } from '../../utils/copyToClipboard';

interface TransactionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: Transaction | null;
  onViewOnSolscan?: (transaction: Transaction) => void;
}

const TransactionDetailsModal: React.FC<TransactionDetailsModalProps> = ({
  isOpen,
  onClose,
  transaction,
  onViewOnSolscan
}) => {
  if (!isOpen || !transaction) return null;

  const formatCurrency = (amount: number) => {
    return `$${(amount / 100).toFixed(2)}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 w-2/3 max-w-4xl shadow-lg rounded-md bg-[#182233]">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-white">Transaction Details</h3>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400">Transaction ID</label>
              <p className="mt-1 text-sm text-white font-mono">{transaction.transactionId}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">Status</label>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full mt-1 ${
                transaction.status === 'SUCCESS' ? 'bg-green-100 text-green-800' :
                transaction.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                transaction.status === 'FAILED' ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {transaction.status}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400">Sender Company</label>
              <p className="mt-1 text-sm text-white">{transaction.senderCompany?.companyName || 'N/A'}</p>
              <div className="flex items-center space-x-2">
                <p className="text-xs text-gray-400 font-mono">
                  {transaction.senderCompany?.companyWallet ? 
                    `${transaction.senderCompany.companyWallet.slice(0, 8)}...${transaction.senderCompany.companyWallet.slice(-8)}` : 
                    'N/A'
                  }
                </p>
                {transaction.senderCompany?.companyWallet && <CopyButton text={transaction.senderCompany.companyWallet} />}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">Receiver Company</label>
              <p className="mt-1 text-sm text-white">{transaction.receiverCompany?.companyName || 'N/A'}</p>
              <div className="flex items-center space-x-2">
                <p className="text-xs text-gray-400 font-mono">
                  {transaction.receiverCompany?.companyWallet ? 
                    `${transaction.receiverCompany.companyWallet.slice(0, 8)}...${transaction.receiverCompany.companyWallet.slice(-8)}` : 
                    'N/A'
                  }
                </p>
                {transaction.receiverCompany?.companyWallet && <CopyButton text={transaction.receiverCompany.companyWallet} />}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400">Amount</label>
              <p className="mt-1 text-sm text-white">{formatCurrency(transaction.amount)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">Protocol Fee</label>
              <p className="mt-1 text-sm text-white">{formatCurrency(transaction.protocolFee)}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400">Amount Received</label>
              <p className="mt-1 text-sm text-white">{formatCurrency(transaction.amountReceived || transaction.amount)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">Exchange Rate</label>
              <p className="mt-1 text-sm text-white">{(transaction.exchangeRate / 100).toFixed(2)}%</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400">Created At</label>
              <p className="mt-1 text-sm text-white">{new Date(transaction.createdAt).toLocaleString()}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">Updated At</label>
              <p className="mt-1 text-sm text-white">{new Date(transaction.updatedAt).toLocaleString()}</p>
            </div>
          </div>

          {transaction.solanaSignature && (
            <div>
              <label className="block text-sm font-medium text-gray-400">Solana Transaction Hash</label>
              <div className="mt-1 flex items-center space-x-2">
                <p className="text-sm text-white font-mono flex-1">{transaction.solanaSignature}</p>
                {onViewOnSolscan && (
                  <button
                    onClick={() => onViewOnSolscan(transaction)}
                    className="text-[#2777EF] hover:text-[#0053E2]"
                    title="View on Solscan"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-[#567095]/30 text-sm font-medium rounded-md text-white bg-[#182233] hover:bg-[#567095]/20"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionDetailsModal;
